import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import type {
  CalendarQueryParams,
  CalendarViewResponse,
  SchedulePostRequest,
  SchedulePostResponse,
  ReschedulePostRequest,
  PostSchedule,
  UnscheduleResponse,
  BestTimeSuggestionsQuery,
  BestTimeSuggestionsResponse,
  ConflictsQueryParams,
  ConflictsResponse,
} from '../types/calendar';

// Re-export calendar types for convenience
export type {
  CalendarQueryParams,
  CalendarViewResponse,
  SchedulePostRequest,
  SchedulePostResponse,
  ReschedulePostRequest,
  PostSchedule,
  UnscheduleResponse,
  BestTimeSuggestionsQuery,
  BestTimeSuggestionsResponse,
  ConflictsQueryParams,
  ConflictsResponse,
  // These types are exported directly from calendar types
  ScheduledPostEntry,
  BestTimeSlot,
  ConflictEntry,
} from '../types/calendar';

// In development, call backend directly. In production, use relative URLs or env var
const API_BASE_URL = import.meta.env.VITE_API_URL ||
  (import.meta.env.DEV ? 'http://localhost:3000' : '');

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Important for cookie-based auth
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track if we're currently refreshing to prevent multiple refresh calls
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

/**
 * Clear all auth state and redirect to login
 * This prevents infinite redirect loops when tokens are invalid
 */
function clearAuthAndRedirect() {
  // Clear zustand persisted auth state
  localStorage.removeItem('auth-storage');

  // Clear any session storage
  sessionStorage.clear();

  // Redirect to login (only if not already there)
  if (!window.location.pathname.includes('/login')) {
    window.location.href = '/login';
  }
}

const processQueue = (error: Error | null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

// Response interceptor for automatic token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // If error is 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Skip refresh for auth endpoints to prevent infinite loops
      if (originalRequest.url?.includes('/auth/login') ||
          originalRequest.url?.includes('/auth/register') ||
          originalRequest.url?.includes('/auth/refresh')) {
        clearAuthAndRedirect();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => apiClient(originalRequest))
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Try to refresh the token
        await apiClient.post('/auth/refresh');

        // Refresh successful, process queued requests
        processQueue(null);

        // Retry the original request
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed, clear queue and redirect to login
        processQueue(refreshError as Error);
        clearAuthAndRedirect();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // For other errors, just reject
    return Promise.reject(error);
  }
);

// ============================================================================
// Dashboard API Functions
// ============================================================================

/**
 * Get pending posts count (for Approve card badge)
 */
export async function getPendingPostsCount(): Promise<number> {
  try {
    const response = await apiClient.get('/content/posts', {
      params: {
        status: 'in_review', // Posts waiting for approval
        limit: 1,
      },
    });
    // Assuming API returns { success: true, data: { posts: [], total: X } }
    return response.data.data?.total || response.data.total || 0;
  } catch (error) {
    console.error('Failed to fetch pending posts count:', error);
    return 0; // Return 0 on error
  }
}

/**
 * Get analytics overview for Quick Stats section
 */
export async function getAnalyticsOverview(timeRange: 'week' | 'month' | 'year' = 'week') {
  try {
    const response = await apiClient.get('/analytics/overview', {
      params: { time_range: timeRange },
    });
    return response.data.data || response.data;
  } catch (error) {
    console.error('Failed to fetch analytics:', error);
    // Return mock data as fallback
    throw error; // Let component handle fallback
  }
}

/**
 * Get upcoming scheduled posts
 */
export async function getUpcomingPosts(limit: number = 10) {
  try {
    const response = await apiClient.get('/content/posts', {
      params: {
        status: 'scheduled',
        limit,
        sort: 'scheduled_at:asc',
      },
    });
    return response.data.data?.posts || response.data.posts || [];
  } catch (error) {
    console.error('Failed to fetch upcoming posts:', error);
    // Return empty array as fallback
    throw error; // Let component handle fallback
  }
}

// ============================================================================
// Publishing Connections API Functions
// ============================================================================

export interface PlatformConnection {
  id: string;
  workspaceId: string;
  platform: string;
  platformUserId: string;
  platformUsername?: string;
  platformAccountName?: string;
  platformProfileUrl?: string;
  isActive: boolean;
  isVerified: boolean;
  connectedAt: string;
  lastUsedAt?: string;
  configurationStatus?: 'active' | 'pending_configuration';
}

export interface CreateConnectionRequest {
  platform: string;
  siteUrl?: string; // For WordPress
  username?: string; // For WordPress
  applicationPassword?: string; // For WordPress
}

export interface CreateConnectionResponse {
  success: boolean;
  authorizationUrl?: string;
  state?: string;
  expiresAt?: string;
  message?: string;
  // For WordPress (no OAuth)
  id?: string;
  platform?: string;
  status?: string;
  platformUsername?: string;
  platformAccountName?: string;
  connectedAt?: string;
}

export interface FacebookPage {
  id: string;
  name: string;
  category: string;
  access_token: string;
  tasks?: string[];
}

export interface InstagramBusinessAccount {
  id: string;
  username: string;
  name?: string;
  profile_picture_url?: string;
  followers_count?: number;
  follows_count?: number;
  media_count?: number;
}

export interface AvailablePagesResponse {
  success: boolean;
  pages: FacebookPage[];
  instagramAccounts?: InstagramBusinessAccount[];
}

export interface FinalizeConnectionRequest {
  pageId: string;
  instagramAccountId?: string;
  token: string;
}

/**
 * Get all platform connections for current workspace
 */
export async function getConnections(): Promise<PlatformConnection[]> {
  try {
    const response = await apiClient.get('/publishing/connections');
    return response.data.connections || [];
  } catch (error) {
    console.error('Failed to fetch connections:', error);
    throw error;
  }
}

/**
 * Create a new platform connection (initiate OAuth or direct connection)
 */
export async function createConnection(
  request: CreateConnectionRequest
): Promise<CreateConnectionResponse> {
  try {
    const response = await apiClient.post('/publishing/connections', request);
    return response.data;
  } catch (error) {
    console.error('Failed to create connection:', error);
    throw error;
  }
}

/**
 * Verify a platform connection
 */
export async function verifyConnection(connectionId: string): Promise<{ isValid: boolean }> {
  try {
    const response = await apiClient.post(`/publishing/connections/${connectionId}/verify`);
    return response.data.verification;
  } catch (error) {
    console.error('Failed to verify connection:', error);
    throw error;
  }
}

/**
 * Delete a platform connection
 */
export async function deleteConnection(connectionId: string): Promise<void> {
  try {
    await apiClient.delete(`/publishing/connections/${connectionId}`);
  } catch (error) {
    console.error('Failed to delete connection:', error);
    throw error;
  }
}

/**
 * Get available Facebook pages and Instagram accounts (two-step OAuth)
 */
export async function getAvailablePages(
  connectionId: string,
  token: string
): Promise<AvailablePagesResponse> {
  try {
    const response = await apiClient.get(`/publishing/connections/${connectionId}/pages`, {
      params: { token },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch available pages:', error);
    throw error;
  }
}

/**
 * Finalize connection by selecting page/account (two-step OAuth)
 */
export async function finalizeConnection(
  connectionId: string,
  request: FinalizeConnectionRequest
): Promise<PlatformConnection> {
  try {
    const response = await apiClient.post(
      `/publishing/connections/${connectionId}/finalize`,
      request
    );
    return response.data.connection;
  } catch (error) {
    console.error('Failed to finalize connection:', error);
    throw error;
  }
}

// =============================================================================
// Billing API Functions
// =============================================================================

/**
 * Get Stripe Customer Portal URL
 * Opens Stripe portal for subscription management (upgrade/downgrade/cancel/payment methods)
 */
export async function getBillingPortalUrl(returnUrl?: string): Promise<{ url: string }> {
  try {
    const response = await apiClient.post('/billing/customer-portal', {
      returnUrl: returnUrl || window.location.href,
    });
    return { url: response.data.data.url };
  } catch (error) {
    console.error('Failed to get billing portal URL:', error);
    throw error;
  }
}

/**
 * Get active subscription for current workspace
 */
export async function getSubscription(): Promise<any> {
  try {
    const response = await apiClient.get('/billing/subscription');
    return response.data.subscription;
  } catch (error) {
    console.error('Failed to get subscription:', error);
    throw error;
  }
}

/**
 * Get wallet balance
 */
export async function getWallet(): Promise<any> {
  try {
    const response = await apiClient.get('/billing/wallet');
    return response.data.wallet;
  } catch (error) {
    console.error('Failed to get wallet:', error);
    throw error;
  }
}

// ============================================================================
// Website API Functions
// ============================================================================

/**
 * Update a website's details
 */
export async function updateWebsite(websiteId: string, data: {
  name?: string;
  description?: string;
  domain?: string;
}): Promise<any> {
  try {
    const response = await apiClient.patch(`/websites/${websiteId}`, data);
    return response.data.data;
  } catch (error) {
    console.error('Failed to update website:', error);
    throw error;
  }
}

/**
 * Set a website as primary
 */
export async function setPrimaryWebsite(websiteId: string): Promise<any> {
  try {
    const response = await apiClient.put(`/websites/${websiteId}/primary`);
    return response.data.data;
  } catch (error) {
    console.error('Failed to set primary website:', error);
    throw error;
  }
}

// ============================================================================
// Content Creation API Functions
// ============================================================================

/**
 * Platform types supported for content generation
 */
export type Platform =
  | 'linkedin'
  | 'instagram'
  | 'facebook'
  | 'twitter'
  | 'youtube'
  | 'tiktok'
  | 'pinterest'
  | 'wordpress'
  | 'blog_summary';

/**
 * Post with all related data (variants, media, etc.)
 */
export interface PostWithRelations {
  post: {
    id: string;
    workspaceId: string;
    createdBy: string;
    title?: string;
    sourceType: 'idea' | 'blog' | 'video' | 'manual';
    sourceUrl?: string;
    sourceText?: string;
    taxonomyNodes: string[];
    status: string;
    internalNotes?: string;
    tokenCost?: number;
    createdAt: string;
    updatedAt: string;
  };
  variants: Array<{
    id: string;
    postId: string;
    platform: string;
    caption: string;
    hashtags: string[];
    mentions?: string[];
    callToAction?: string;
    characterCount: number;
    isWithinLimits: boolean;
    aiModel?: string;
    promptTokens?: number;
    completionTokens?: number;
    createdAt: string;
  }>;
  mediaAssets: Array<{
    id: string;
    postId: string;
    type: 'image' | 'video' | 'audio' | 'document';
    s3Key: string;
    url: string;
    fileSize: number;
    mimeType: string;
    width?: number;
    height?: number;
    duration?: number;
    createdAt: string;
  }>;
  transcripts: any[];
  clips: any[];
}

/**
 * Request to generate content from idea
 */
export interface GenerateFromIdeaRequest {
  idea: string;
  taxonomyNodeIds?: string[];
  platforms?: Platform[];
  title?: string;
  internalNotes?: string;
  language?: string;
  savedIdeaId?: string;
}

/**
 * Request to generate content from blog URL or text
 */
export interface GenerateFromBlogRequest {
  url?: string;
  text?: string;
  taxonomyNodeIds?: string[];
  platforms?: Platform[];
  title?: string;
  internalNotes?: string;
  language?: string;
}

/**
 * Request to generate content from video (multipart/form-data)
 */
export interface GenerateFromVideoRequest {
  taxonomyNodeIds?: string[];
  platforms?: Platform[];
  title?: string;
  internalNotes?: string;
  extractClips?: boolean;
  clipCount?: number;
  language?: string;
}

/**
 * Job created response for async content generation
 */
export interface JobCreatedResponse {
  jobId: string;
  message: string;
}

/**
 * Job status response for async operations
 */
export interface JobStatusResponse {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number; // 0-100
  type: 'idea' | 'blog' | 'video' | 'manual' | 'content_ideas';
  resultPostId?: string;
  errorMessage?: string;
  errorCode?: string;
  startedAt?: string;
  completedAt?: string;
  duration?: number;
  statusMessage?: string | null; // Current phase/step (e.g. "Transcribing audio...")
  tokensUsed?: number; // Cumulative tokens used during processing
  result?: {
    ideas?: ContentIdea[];
    tokensUsed?: number;
    trendsAvailable?: boolean;
    trendingTopicsCount?: number;
  };
}

export interface ContentIdea {
  id: string;
  title: string;
  description: string;
  trendContext?: {
    topic: string;
    traffic?: string;
    relevanceScore: number;
  };
  suggestedPlatforms: Platform[];
  estimatedEngagement: 'low' | 'medium' | 'high';
}

export interface SavedContentIdea {
  id: string;
  workspaceId: string;
  userId: string;
  title: string;
  description: string;
  trendTopic?: string | null;
  trendTraffic?: string | null;
  trendRelevanceScore?: string | null;
  suggestedPlatforms: Platform[];
  estimatedEngagement: 'low' | 'medium' | 'high';
  generatedFromGeo?: string | null;
  generatedFromKeywords?: string[] | null;
  savedAt: string;
  usedAt?: string | null;
  usedPostId?: string | null;
  useCount: number;
  isArchived: boolean;
}

export interface SuggestIdeasRequest {
  geo?: string;
  keywords?: string[];
  limit?: number;
  language?: string;
}

/**
 * Generate content from a simple idea/text description
 * Returns job ID for tracking async generation
 */
export async function generateFromIdea(
  data: GenerateFromIdeaRequest
): Promise<JobCreatedResponse> {
  try {
    const response = await apiClient.post('/content/create/idea', data);
    return response.data.data;
  } catch (error) {
    console.error('Failed to generate content from idea:', error);
    throw error;
  }
}

/**
 * Generate content from blog URL or pasted text
 * Returns job ID for tracking async generation
 */
export async function generateFromBlog(
  data: GenerateFromBlogRequest
): Promise<JobCreatedResponse> {
  try {
    const response = await apiClient.post('/content/create/blog', data);
    return response.data.data;
  } catch (error) {
    console.error('Failed to generate content from blog:', error);
    throw error;
  }
}

/**
 * Generate content from video file
 * Uploads video, transcribes audio, extracts clips, generates variants
 * Returns job ID for async processing or immediate result
 */
export async function generateFromVideo(
  fileOrUrl: File | string,
  options: GenerateFromVideoRequest
): Promise<PostWithRelations | { jobId: string }> {
  try {
    const formData = new FormData();

    // Handle both File upload and YouTube URL
    if (typeof fileOrUrl === 'string') {
      // YouTube URL
      formData.append('url', fileOrUrl);
    } else {
      // File upload
      formData.append('video', fileOrUrl);
    }

    // Append optional fields
    if (options.taxonomyNodeIds && options.taxonomyNodeIds.length > 0) {
      options.taxonomyNodeIds.forEach((id) => formData.append('taxonomyNodeIds', id));
    }
    if (options.platforms && options.platforms.length > 0) {
      options.platforms.forEach((platform) => formData.append('platforms', platform));
    }
    if (options.title) formData.append('title', options.title);
    if (options.internalNotes) formData.append('internalNotes', options.internalNotes);
    if (options.extractClips !== undefined)
      formData.append('extractClips', String(options.extractClips));
    if (options.clipCount) formData.append('clipCount', String(options.clipCount));
    if (options.language) formData.append('language', options.language);

    const response = await apiClient.post('/content/create/video', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000, // 2 minutes for video uploads
    });

    return response.data.data;
  } catch (error) {
    console.error('Failed to generate content from video:', error);
    throw error;
  }
}

/**
 * Get job status for async video processing
 * Poll this endpoint to track progress
 */
export async function getJobStatus(jobId: string): Promise<JobStatusResponse> {
  try {
    const response = await apiClient.get(`/content/jobs/${jobId}`);
    return response.data.data;
  } catch (error) {
    console.error('Failed to get job status:', error);
    throw error;
  }
}

/**
 * Get count of active jobs (pending + processing) for workspace
 */
export async function getActiveJobsCount(type?: string): Promise<{ count: number; workspaceId: string }> {
  try {
    const response = await apiClient.get('/content/jobs/active/count', {
      params: type ? { type } : undefined,
    });
    return response.data.data;
  } catch (error) {
    console.error('Failed to get active jobs count:', error);
    throw error;
  }
}

/**
 * List all active jobs for workspace
 */
export async function listActiveJobs(type?: string): Promise<{
  jobs: Array<{
    id: string;
    type: string;
    status: string;
    progress: number;
    inputData: any;
    createdAt: string;
    startedAt: string | null;
  }>;
  count: number;
}> {
  try {
    const response = await apiClient.get('/content/jobs', {
      params: type ? { type } : undefined,
    });
    return response.data.data;
  } catch (error) {
    console.error('Failed to list active jobs:', error);
    throw error;
  }
}

/**
 * Cancel a pending or processing job
 */
export async function cancelJob(jobId: string): Promise<void> {
  try {
    await apiClient.delete(`/content/jobs/${jobId}`);
  } catch (error) {
    console.error('Failed to cancel job:', error);
    throw error;
  }
}

/**
 * Start async job for AI-powered content ideas generation
 * Returns jobId for tracking progress
 */
export async function suggestContentIdeas(request: SuggestIdeasRequest): Promise<{ jobId: string; message: string }> {
  try {
    const response = await apiClient.post('/content/suggest-ideas', request);
    return response.data.data;
  } catch (error) {
    console.error('Failed to start content ideas generation:', error);
    throw error;
  }
}

/**
 * Save a content idea to the gallery for later use
 */
export async function saveContentIdea(
  idea: ContentIdea,
  metadata?: { geo?: string; keywords?: string[] }
): Promise<SavedContentIdea> {
  try {
    const response = await apiClient.post('/content/ideas/save', {
      title: idea.title,
      description: idea.description,
      trendContext: idea.trendContext,
      suggestedPlatforms: idea.suggestedPlatforms,
      estimatedEngagement: idea.estimatedEngagement,
      geo: metadata?.geo,
      keywords: metadata?.keywords,
    });
    return response.data.data;
  } catch (error) {
    console.error('Failed to save content idea:', error);
    throw error;
  }
}

/**
 * Get list of saved content ideas with pagination
 */
export async function getSavedIdeas(params?: {
  limit?: number;
  offset?: number;
  includeArchived?: boolean;
  userId?: string;
}): Promise<SavedContentIdea[]> {
  try {
    const response = await apiClient.get('/content/ideas', { params });
    return response.data.data;
  } catch (error) {
    console.error('Failed to get saved ideas:', error);
    throw error;
  }
}

/**
 * Delete a saved content idea
 */
export async function deleteSavedIdea(ideaId: string): Promise<void> {
  try {
    await apiClient.delete(`/content/ideas/${ideaId}`);
  } catch (error) {
    console.error('Failed to delete saved idea:', error);
    throw error;
  }
}

/**
 * Mark a saved idea as used when creating content from it
 */
export async function markIdeaAsUsed(ideaId: string, postId: string): Promise<void> {
  try {
    await apiClient.patch(`/content/ideas/${ideaId}/use`, { postId });
  } catch (error) {
    console.error('Failed to mark idea as used:', error);
    throw error;
  }
}

// ============================================================================
// Post Management API Functions
// ============================================================================

/**
 * List posts with filters and pagination
 */
export interface ListPostsQuery {
  status?: string;
  sourceType?: string;
  platform?: string;
  createdBy?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
  sortBy?: 'createdAt' | 'updatedAt' | 'publishedAt';
  sortOrder?: 'asc' | 'desc';
}

export interface ListPostsResponse {
  posts: PostWithRelations[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Get list of posts with optional filters
 */
export async function listPosts(query: ListPostsQuery = {}): Promise<ListPostsResponse> {
  try {
    const response = await apiClient.get('/content/posts', { params: query });

    // Backend returns { success, data: PostWithRelations[], meta: { total, limit, offset, hasMore } }
    const postsArray = response.data.data || [];
    const meta = response.data.meta || { total: 0, limit: query.limit || 20, offset: query.offset || 0 };

    return {
      posts: postsArray,
      total: meta.total,
      limit: meta.limit,
      offset: meta.offset,
    };
  } catch (error) {
    console.error('Failed to list posts:', error);
    throw error;
  }
}

/**
 * Get single post with all variants and media
 */
export async function getPost(postId: string): Promise<PostWithRelations> {
  try {
    const response = await apiClient.get(`/content/posts/${postId}`);
    return response.data.data;
  } catch (error) {
    console.error('Failed to get post:', error);
    throw error;
  }
}

/**
 * Update post metadata (status, title, notes, taxonomy)
 */
export interface UpdatePostRequest {
  title?: string;
  internalNotes?: string;
  status?: string;
  taxonomyNodeIds?: string[];
}

export async function updatePost(
  postId: string,
  updates: UpdatePostRequest
): Promise<PostWithRelations> {
  try {
    const response = await apiClient.patch(`/content/posts/${postId}`, updates);
    return response.data.data;
  } catch (error) {
    console.error('Failed to update post:', error);
    throw error;
  }
}

/**
 * Delete post (soft delete)
 */
export async function deletePost(postId: string): Promise<void> {
  try {
    await apiClient.delete(`/content/posts/${postId}`);
  } catch (error) {
    console.error('Failed to delete post:', error);
    throw error;
  }
}

// ============================================================================
// IMAGE GENERATION
// ============================================================================

export type ImageProvider = 'openai_dalle2' | 'openai_dalle3' | 'gemini' | 'stable_diffusion' | 'flux';
export type ImageQuality = 'standard' | 'hd';
export type ImageStyle = 'vivid' | 'natural';
export type AspectRatio = '1:1' | '16:9' | '9:16' | '4:5' | '3:4' | '2:3' | 'custom';
export type ImageGenerationStatus = 'pending' | 'generating' | 'completed' | 'failed';
export type ImageVariantStatus = 'generated' | 'approved' | 'rejected';

export interface ImageVariant {
  id: string;
  imageGenerationJobId: string;
  mediaAssetId: string;
  platform: string;
  prompt: string;
  negativePrompt?: string;
  provider: ImageProvider;
  model: string;
  width: number;
  height: number;
  aspectRatio: AspectRatio;
  quality?: ImageQuality;
  style?: ImageStyle;
  status: ImageVariantStatus;
  approvedBy?: string;
  approvedAt?: string;
  generationTimeMs?: number;
  costTokens?: number;
  revisedPrompt?: string;
  metadata?: any;
  mediaAsset?: {
    id: string;
    storageUrl: string;
    storageKey: string;
    width: number;
    height: number;
    fileSize: number;
    mimeType: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ImageGenerationJobStatus {
  jobId: string;
  status: ImageGenerationStatus;
  progress: number;
  totalVariantsRequested: number;
  totalVariantsGenerated: number;
  variants: ImageVariant[];
  errorMessage?: string;
  startedAt?: string;
  completedAt?: string;
}

export interface GenerateImagesRequest {
  postId: string;
  provider: ImageProvider;
  model?: string;
  platforms: string[];
  variantsPerPlatform?: number;
  aspectRatioOverrides?: Record<string, AspectRatio>;
  customDimensions?: Record<string, { width: number; height: number }>;
  quality?: ImageQuality;
  style?: ImageStyle;
}

export interface GenerateImagesResponse {
  jobId: string;
  status: ImageGenerationStatus;
  progress: number;
  totalVariantsRequested: number;
  estimatedCostTokens: number;
}

export interface ImageProviderInfo {
  provider: ImageProvider;
  defaultModel: string;
  capabilities: {
    supportedModels: string[];
    supportedAspectRatios: AspectRatio[];
    supportedQualities: ImageQuality[];
    supportedStyles: ImageStyle[];
    supportsNegativePrompt: boolean;
    supportsPromptRevision: boolean;
    maxVariantsPerRequest: number;
    minDimension: number;
    maxDimension: number;
  };
}

/**
 * Generate images for a post
 * POST /image-generation/generate
 */
export async function generateImages(request: GenerateImagesRequest): Promise<GenerateImagesResponse> {
  try {
    const response = await apiClient.post('/image-generation/generate', request);
    return response.data.data;
  } catch (error) {
    console.error('Failed to generate images:', error);
    throw error;
  }
}

/**
 * Get image generation job status
 * GET /image-generation/jobs/:jobId
 */
export async function getImageGenerationJobStatus(jobId: string): Promise<ImageGenerationJobStatus> {
  try {
    const response = await apiClient.get(`/image-generation/jobs/${jobId}`);
    return response.data.data;
  } catch (error) {
    console.error('Failed to get job status:', error);
    throw error;
  }
}

/**
 * Approve image variants
 * POST /image-generation/approve
 */
export async function approveImageVariants(variantIds: string[]): Promise<void> {
  try {
    await apiClient.post('/image-generation/approve', { variantIds });
  } catch (error) {
    console.error('Failed to approve variants:', error);
    throw error;
  }
}

/**
 * Reject image variants
 * POST /image-generation/reject
 */
export async function rejectImageVariants(variantIds: string[]): Promise<void> {
  try {
    await apiClient.post('/image-generation/reject', { variantIds });
  } catch (error) {
    console.error('Failed to reject variants:', error);
    throw error;
  }
}

/**
 * Link approved images to post variants
 * POST /image-generation/link
 */
export async function linkImagesToPost(postId: string, assignments: Record<string, string>): Promise<void> {
  try {
    await apiClient.post('/image-generation/link', { postId, assignments });
  } catch (error) {
    console.error('Failed to link images:', error);
    throw error;
  }
}

/**
 * Get available image generation providers
 * GET /image-generation/providers
 */
export async function getImageProviders(includeCapabilities: boolean = false): Promise<{
  providers: ImageProvider[] | ImageProviderInfo[];
  defaultProvider: ImageProvider;
}> {
  try {
    const response = await apiClient.get('/image-generation/providers', {
      params: { includeCapabilities },
    });
    return response.data.data;
  } catch (error) {
    console.error('Failed to get providers:', error);
    throw error;
  }
}

/**
 * Estimate cost for image generation
 * POST /image-generation/estimate-cost
 */
export async function estimateImageGenerationCost(request: {
  provider: ImageProvider;
  model?: string;
  platforms: string[];
  variantsPerPlatform: number;
  aspectRatioOverrides?: Record<string, AspectRatio>;
  customDimensions?: Record<string, { width: number; height: number }>;
  quality?: ImageQuality;
}): Promise<{
  provider: ImageProvider;
  model: string;
  totalImages: number;
  estimatedCost: {
    tokens: number;
    usd: number;
  };
  breakdown: any;
}> {
  try {
    const response = await apiClient.post('/image-generation/estimate-cost', request);
    return response.data.data;
  } catch (error) {
    console.error('Failed to estimate cost:', error);
    throw error;
  }
}

// ============================================================================
// Calendar & Scheduling API Functions
// ============================================================================

/**
 * Get calendar view with scheduled posts grouped by date
 */
export async function getCalendarView(params: CalendarQueryParams): Promise<CalendarViewResponse> {
  try {
    const response = await apiClient.get('/calendar', { params });
    return response.data.data || response.data;
  } catch (error) {
    console.error('Failed to fetch calendar view:', error);
    throw error;
  }
}

/**
 * Schedule a post for publishing
 */
export async function schedulePost(data: SchedulePostRequest): Promise<SchedulePostResponse> {
  try {
    const response = await apiClient.post('/calendar/schedule', data);
    return response.data.data || response.data;
  } catch (error) {
    console.error('Failed to schedule post:', error);
    throw error;
  }
}

/**
 * Reschedule an existing scheduled post
 */
export async function reschedulePost(
  postId: string,
  data: ReschedulePostRequest
): Promise<PostSchedule> {
  try {
    const response = await apiClient.patch(`/calendar/${postId}/reschedule`, data);
    return response.data.data || response.data;
  } catch (error) {
    console.error('Failed to reschedule post:', error);
    throw error;
  }
}

/**
 * Remove a post from the schedule (unschedule)
 */
export async function unschedulePost(postId: string): Promise<UnscheduleResponse> {
  try {
    const response = await apiClient.delete(`/calendar/${postId}/unschedule`);
    return response.data.data || response.data;
  } catch (error) {
    console.error('Failed to unschedule post:', error);
    throw error;
  }
}

/**
 * Get AI-driven best time to post suggestions
 */
export async function getBestTimeSuggestions(
  params?: BestTimeSuggestionsQuery
): Promise<BestTimeSuggestionsResponse> {
  try {
    const response = await apiClient.get('/calendar/suggestions', { params });
    return response.data.data || response.data;
  } catch (error) {
    console.error('Failed to fetch best time suggestions:', error);
    throw error;
  }
}

/**
 * Get scheduling conflicts
 */
export async function getConflicts(params?: ConflictsQueryParams): Promise<ConflictsResponse> {
  try {
    const response = await apiClient.get('/calendar/conflicts', { params });
    return response.data.data || response.data;
  } catch (error) {
    console.error('Failed to fetch scheduling conflicts:', error);
    throw error;
  }
}

export default apiClient;
